// Tableau de présence : jour, semaine, mois, année.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Grid, IconButton, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, FileDownloadOutlined } from '@mui/icons-material';
import Layout from '../../components/Layout';
import StatutChip from '../../components/rh/StatutChip';
import { rhAPI } from '../../services/api';
import { apiError } from '../../utils/format';
import {
  STATUTS, MOIS_COURTS, dayNum, dayShort, downloadCsv, duree, isoDow, periodLabel, periodRange, shiftRef, todayIso,
} from '../../utils/rh';
import { tokens } from '../../theme';

const MODES = [['jour', 'Jour'], ['semaine', 'Semaine'], ['mois', 'Mois'], ['annee', 'Année']];
const pct = (v) => (v === null || v === undefined ? '—' : `${String(v).replace('.', ',')} %`);

const Kpi = ({ label, value, sub, color }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: '14px !important' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontFamily: tokens.mono, fontSize: 24, fontWeight: 600, color, mt: 0.25 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

const TOTAL_COLS = [
  ['presents', 'Prés.', 'Jours de présence'],
  ['retards', 'Ret.', 'Nombre de retards'],
  ['minutes_retard', 'Durée ret.', 'Cumul des retards'],
  ['absences_justifiees', 'Abs. J', 'Absences justifiées (jours)'],
  ['absences_injustifiees', 'Abs. I', 'Absences injustifiées (jours)'],
  ['non_pointes', 'N.P.', 'Jours ouvrés sans pointage ni absence déclarée'],
  ['conges', 'Cong.', 'Jours de congé'],
  ['minutes_travail', 'Heures', 'Heures travaillées (pause déduite)'],
  ['taux_presence', 'Taux', 'Présences / jours ouvrés écoulés'],
];
const fmtTotal = (k, v) => (k === 'minutes_retard' || k === 'minutes_travail' ? duree(v) : k === 'taux_presence' ? pct(v) : v);

function dayTitle(d) {
  const s = STATUTS[d.statut]?.label || d.statut;
  const parts = [s];
  if (d.arrivee) parts.push(`${d.arrivee} → ${d.depart || '…'}`);
  if (d.retard_min) parts.push(`retard ${duree(d.retard_min)}${d.retard_justifie ? ' (justifié)' : ''}`);
  if (d.detail && !d.arrivee) parts.push(d.detail.toLowerCase());
  return parts.join(' · ');
}

const RhPresence = () => {
  const [mode, setMode] = useState('mois');
  const [ref, setRef] = useState(todayIso());
  const [employeId, setEmployeId] = useState('');
  const [employes, setEmployes] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const { from, to } = periodRange(mode, ref);

  useEffect(() => { rhAPI.employes(true).then((r) => setEmployes(r.data)).catch(() => {}); }, []);
  const load = useCallback(() => {
    setError('');
    rhAPI.rapport({ from, to, ...(employeId ? { employe_id: employeId } : {}), ...(mode === 'annee' ? { mois: 1 } : {}) })
      .then((r) => setData({ ...r.data, mode })).catch((e) => setError(apiError(e)));
  }, [from, to, employeId, mode]);
  useEffect(() => { load(); }, [load]);

  // Mode des données affichées (peut différer brièvement du mode choisi pendant le chargement)
  const shown = data?.mode;
  const t = data?.totaux || {};
  const days = data?.employes?.[0]?.jours?.map((d) => d.date) || [];
  const offDay = (d) => !(data?.jours_travailles || []).includes(isoDow(d)) || data?.feries?.[d];

  const exportCsv = () => {
    const name = `presence_${shown}_${data.from}_${data.to}.csv`;
    const totalHeader = TOTAL_COLS.map(([, l]) => l);
    if (shown === 'annee') {
      const header = ['Matricule', 'Nom', 'Prénom', ...MOIS_COURTS.map((m) => `Taux ${m}`), ...totalHeader];
      const rows = data.employes.map((e) => [e.matricule, e.nom, e.prenom,
        ...MOIS_COURTS.map((_, i) => { const k = `${data.from.slice(0, 4)}-${String(i + 1).padStart(2, '0')}`; return e.mois?.[k]?.taux_presence ?? ''; }),
        ...TOTAL_COLS.map(([k]) => fmtTotal(k, e.totaux[k]))]);
      downloadCsv(name, header, rows);
    } else {
      const header = ['Matricule', 'Nom', 'Prénom', ...days.map((d) => `${dayShort(d)} ${dayNum(d)}`), ...totalHeader];
      const rows = data.employes.map((e) => [e.matricule, e.nom, e.prenom,
        ...e.jours.map((d) => (d.arrivee ? `${STATUTS[d.statut].code} ${d.arrivee}-${d.depart || ''}` : STATUTS[d.statut]?.code || '')),
        ...TOTAL_COLS.map(([k]) => fmtTotal(k, e.totaux[k]))]);
      downloadCsv(name, header, rows);
    }
  };

  const head = { fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', px: 0.75 };
  const sticky = { position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, minWidth: 170 };

  return (
    <Layout title="Tableau de présence" plain
      actions={<Button variant="outlined" startIcon={<FileDownloadOutlined />} disabled={!data?.employes?.length} onClick={exportCsv}>Exporter (Excel)</Button>}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_, v) => v && setMode(v)} aria-label="Période">
          {MODES.map(([k, l]) => <ToggleButton key={k} value={k} sx={{ px: 2 }}>{l}</ToggleButton>)}
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton aria-label="Période précédente" onClick={() => setRef(shiftRef(mode, ref, -1))}><ChevronLeft /></IconButton>
          <Typography sx={{ fontWeight: 600, minWidth: 220, textAlign: 'center', textTransform: mode === 'jour' ? 'capitalize' : 'none' }}>{periodLabel(mode, ref)}</Typography>
          <IconButton aria-label="Période suivante" onClick={() => setRef(shiftRef(mode, ref, 1))}><ChevronRight /></IconButton>
          <Button size="small" onClick={() => setRef(todayIso())}>Aujourd'hui</Button>
        </Box>
        <TextField select size="small" label="Employé" value={employeId} sx={{ minWidth: 220, ml: 'auto' }} onChange={(e) => setEmployeId(e.target.value)}>
          <MenuItem value="">Tout le personnel</MenuItem>
          {employes.map((e) => <MenuItem key={e.id} value={e.id}>{e.nom} {e.prenom}</MenuItem>)}
        </TextField>
      </Box>

      {data && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} md={2}><Kpi label="Taux de présence" value={pct(t.taux_presence)} sub={`${t.presents || 0} / ${t.jours_ouvres || 0} jours ouvrés`} /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Retards" value={t.retards || 0} sub={`${duree(t.minutes_retard)} cumulés · ${t.retards_justifies || 0} justifiés`} color={STATUTS.RETARD.fg} /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Absences justifiées" value={t.absences_justifiees || 0} sub="jours" color={STATUTS.ABSENT_J.fg} /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Absences injustifiées" value={t.absences_injustifiees || 0} sub="jours" color={STATUTS.ABSENT_NJ.fg} /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Non pointés" value={t.non_pointes || 0} sub="jours à régulariser" color={STATUTS.NON_POINTE.fg} /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Congés" value={t.conges || 0} sub={`jours · ${duree(t.minutes_travail)} travaillées`} color={STATUTS.CONGE.fg} /></Grid>
        </Grid>
      )}

      <Card variant="outlined">
        <TableContainer sx={{ maxHeight: '70vh' }}>
          {data && shown === 'jour' && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Employé</TableCell><TableCell>Statut</TableCell><TableCell>Arrivée</TableCell><TableCell>Départ</TableCell>
                  <TableCell>Retard</TableCell><TableCell>Départ anticipé</TableCell><TableCell align="right">Travail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.employes.map((e) => {
                  const d = e.jours[0];
                  return (
                    <TableRow key={e.id} hover>
                      <TableCell><b>{e.nom} {e.prenom}</b> <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>{e.matricule}</Typography></TableCell>
                      <TableCell><StatutChip statut={d.statut} /></TableCell>
                      <TableCell sx={{ fontFamily: tokens.mono }}>{d.arrivee || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: tokens.mono }}>{d.depart || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: tokens.mono }}>{d.retard_min ? `${duree(d.retard_min)}${d.retard_justifie ? ' (justifié)' : ''}` : '—'}</TableCell>
                      <TableCell sx={{ fontFamily: tokens.mono }}>{d.depart_anticipe_min ? duree(d.depart_anticipe_min) : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{d.travail_min ? duree(d.travail_min) : ''}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {data && (shown === 'semaine' || shown === 'mois') && (
            <Table size="small" stickyHeader sx={{ '& td, & th': { borderRight: '1px solid', borderRightColor: 'divider' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...head, ...sticky, zIndex: 3 }}>Employé</TableCell>
                  {days.map((d) => (
                    <TableCell key={d} align="center" title={data.feries[d] || ''}
                      sx={{ ...head, bgcolor: offDay(d) ? '#F4F5F7' : undefined, color: d === data.today ? 'primary.main' : undefined }}>
                      <Box sx={{ fontSize: 10.5, fontWeight: 500, color: 'text.secondary' }}>{dayShort(d)}</Box>{dayNum(d)}
                    </TableCell>
                  ))}
                  {TOTAL_COLS.map(([k, l, title]) => <TableCell key={k} align="right" title={title} sx={{ ...head, bgcolor: '#F7F8FA' }}>{l}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.employes.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ ...sticky, py: 0.5 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{e.nom} {e.prenom}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{e.matricule}</Typography>
                    </TableCell>
                    {e.jours.map((d) => (
                      <TableCell key={d.date} align="center" sx={{ px: 0.25, py: 0.5, bgcolor: offDay(d.date) ? '#FAFAFB' : undefined }}>
                        <StatutChip compact statut={d.statut} title={dayTitle(d)} />
                      </TableCell>
                    ))}
                    {TOTAL_COLS.map(([k]) => (
                      <TableCell key={k} align="right" sx={{ fontFamily: tokens.mono, fontSize: 12.5, whiteSpace: 'nowrap', px: 0.75, bgcolor: '#FBFBFC' }}>
                        {fmtTotal(k, e.totaux[k])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {data && shown === 'annee' && (
            <Table size="small" stickyHeader sx={{ '& td, & th': { borderRight: '1px solid', borderRightColor: 'divider' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...head, ...sticky, zIndex: 3 }}>Employé</TableCell>
                  {MOIS_COURTS.map((m, i) => (
                    <TableCell key={m} align="center" sx={{ ...head, cursor: 'pointer' }} title="Ouvrir le mois"
                      onClick={() => { setMode('mois'); setRef(`${data.from.slice(0, 4)}-${String(i + 1).padStart(2, '0')}-01`); }}>{m}</TableCell>
                  ))}
                  {TOTAL_COLS.map(([k, l, title]) => <TableCell key={k} align="right" title={title} sx={{ ...head, bgcolor: '#F7F8FA' }}>{l}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.employes.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ ...sticky, py: 0.5 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{e.nom} {e.prenom}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{e.matricule}</Typography>
                    </TableCell>
                    {MOIS_COURTS.map((_, i) => {
                      const m = e.mois?.[`${data.from.slice(0, 4)}-${String(i + 1).padStart(2, '0')}`];
                      const rate = m?.taux_presence;
                      return (
                        <TableCell key={i} align="center" sx={{ px: 0.5, py: 0.5 }}>
                          {rate === null || rate === undefined ? <Typography sx={{ color: '#C3C9D1', fontSize: 12 }}>—</Typography> : (
                            <Tooltip title={`${m.presents} présences · ${m.retards} retards · ${m.absences_justifiees + m.absences_injustifiees} absences · ${m.conges} jours de congé`}>
                              <Box>
                                <Typography sx={{ fontFamily: tokens.mono, fontSize: 12.5, fontWeight: 600, color: rate >= 95 ? 'success.main' : rate >= 85 ? 'warning.main' : 'error.main' }}>
                                  {Math.round(rate)}%
                                </Typography>
                                <Typography sx={{ fontSize: 10.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                  {m.retards ? `${m.retards}R ` : ''}{m.absences_justifiees + m.absences_injustifiees ? `${m.absences_justifiees + m.absences_injustifiees}A ` : ''}{m.conges ? `${m.conges}C` : ''}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                      );
                    })}
                    {TOTAL_COLS.map(([k]) => (
                      <TableCell key={k} align="right" sx={{ fontFamily: tokens.mono, fontSize: 12.5, whiteSpace: 'nowrap', px: 0.75, bgcolor: '#FBFBFC' }}>
                        {fmtTotal(k, e.totaux[k])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {data && data.employes.length === 0 && <Typography sx={{ p: 4, textAlign: 'center' }}>Aucun employé sur cette période.</Typography>}
        </TableContainer>
      </Card>

      {mode !== 'annee' && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5, alignItems: 'center' }}>
          {['PRESENT', 'RETARD', 'ABSENT_J', 'ABSENT_NJ', 'NON_POINTE', 'CONGE', 'FERIE', 'SUPPLEMENTAIRE'].map((s) => (
            <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <StatutChip compact statut={s} /><Typography sx={{ fontSize: 12.5 }}>{STATUTS[s].label}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {mode === 'annee' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Taux de présence mensuel ; R = retards, A = jours d'absence, C = jours de congé. Cliquez sur un mois pour le détail.
        </Typography>
      )}
    </Layout>
  );
};

export default RhPresence;
